import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AdminLayout from "../../Layout/AdminLayout";
import AvatarImage from "../../Components/AvatarImage/AvatarImage";
import { SkeletonBlock, SkeletonText } from "../../Components/Skeleton/Skeleton";
import "./PatientMedicalSubmissions.scss";
import {
  clearPrescriptionDetail,
  clearPrescriptionError,
} from "../../Features/Auth/Slice/prescriptionSlice";
import {
  acceptAdminPrescription,
  getAdminPrescriptionById,
  rejectAdminPrescription,
} from "../../Features/Auth/Thunk/prescriptionThunk";
import { showAlert } from "../../feedback/feedbackApi";

const fallbackPatient = {
  patientName: "Patient",
  location: "",
  avatar: "",
  previewImage: "",
};

const VALID_TABS = ["prescription", "report", "insurance"];

function tabFromQuery(raw) {
  const t = String(raw || "").toLowerCase();
  return VALID_TABS.includes(t) ? t : "prescription";
}

export default function PatientMedicalSubmissions() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const prescriptionId =
    searchParams.get("prescriptionId") || location.state?.prescriptionId || "";

  const returnTo = searchParams.get("returnTo") || "";

  const [activeTab, setActiveTab] = useState(() =>
    tabFromQuery(searchParams.get("tab") || location.state?.activeTab)
  );

  /** Legacy navigation via `state` only — mirror into the URL so refresh and back/forward work. */
  useEffect(() => {
    const st = location.state?.prescriptionId;
    if (!st) return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (p.get("prescriptionId")) return prev;
        p.set("prescriptionId", String(st));
        p.set("tab", tabFromQuery(location.state?.activeTab));
        if (location.state?.returnTo) {
          p.set("returnTo", String(location.state.returnTo));
        }
        return p;
      },
      { replace: true }
    );
  }, [location.state, setSearchParams]);

  useEffect(() => {
    setActiveTab(tabFromQuery(searchParams.get("tab")));
  }, [searchParams]);

  const setTab = useCallback(
    (nextTab) => {
      const t = tabFromQuery(nextTab);
      setActiveTab(t);
      const next = new URLSearchParams(searchParams);
      next.set("tab", t);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const goBackToClinical = useCallback(() => {
    const tab = tabFromQuery(searchParams.get("tab"));
    const pid = prescriptionId;
    navigate(
      `/prescriptions-reports?tab=${encodeURIComponent(tab)}${
        pid ? `&highlight=prescription_${encodeURIComponent(pid)}` : ""
      }`
    );
  }, [navigate, prescriptionId, searchParams]);

  const handleClose = useCallback(() => {
    if (returnTo === "clinical") {
      goBackToClinical();
      return;
    }
    if (typeof window !== "undefined" && window.history.length > 1) {
      navigate(-1);
      return;
    }
    navigate("/Dashboard");
  }, [returnTo, goBackToClinical, navigate]);

  const {
    currentPatient,
    currentDocuments,
    detailLoading,
    actionLoading,
    error,
    successMessage,
  } = useSelector((state) => state.prescriptions);

  useEffect(() => {
    if (prescriptionId) {
      dispatch(getAdminPrescriptionById(prescriptionId));
    }

    return () => {
      dispatch(clearPrescriptionDetail());
      dispatch(clearPrescriptionError());
    };
  }, [dispatch, prescriptionId]);

  const patient = currentPatient || fallbackPatient;
  const requestCategorySubcategory = useMemo(() => {
    const docs = Array.isArray(currentDocuments) ? currentDocuments : [];
    for (const doc of docs) {
      const label = String(doc?.requestCategorySubcategory || "").trim();
      if (label) return label;
    }
    return "";
  }, [currentDocuments]);

  const filteredDocuments = useMemo(() => {
    const docs = currentDocuments || [];

    const typeMatch = (item) => {
      const t = String(item?.type || "").toLowerCase();
      if (activeTab === "prescription") return t === "prescription";
      if (activeTab === "report") return t === "report";
      return t === "insurance";
    };

    return docs.filter(typeMatch);
  }, [currentDocuments, activeTab]);
  const [activeDocId, setActiveDocId] = useState("");
  useEffect(() => {
    const nextId = filteredDocuments?.[0]?.id ? String(filteredDocuments[0].id) : "";
    setActiveDocId((prev) => {
      if (!prev) return nextId;
      const stillExists = filteredDocuments.some((doc) => String(doc.id) === String(prev));
      return stillExists ? prev : nextId;
    });
  }, [filteredDocuments]);

  const activePreviewDoc =
    filteredDocuments.find((doc) => String(doc.id) === String(activeDocId)) ||
    filteredDocuments?.[0] ||
    null;
  const activePreviewSrc = activePreviewDoc
    ? activePreviewDoc.url ||
      activePreviewDoc.previewImage ||
      patient.previewImage ||
      fallbackPatient.previewImage ||
      ""
    : "";
  const activeStatus = String(activePreviewDoc?.status || "").toLowerCase();
  const isAccepted = activeStatus === "accepted";
  const isRejected = activeStatus === "rejected";
  const isDecisionTaken = isAccepted || isRejected;
  const currentTabLabel =
    activeTab === "prescription" ? "Prescription" : activeTab === "report" ? "Medical Report" : "Insurance";
  const currentTabCount = filteredDocuments.length;
  const currentTabNoun =
    activeTab === "prescription"
      ? "prescription"
      : activeTab === "report"
        ? "medical report"
        : "insurance document";

  const handleView = (doc) => {
    const targetUrl = doc?.url || doc?.previewImage || "";
    if (targetUrl) {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
      return;
    }
    showAlert("No file URL is available for this document.", { variant: "warning", title: "Cannot open file" });
  };

  const handleAccept = async () => {
    if (!prescriptionId) return;
    if (isDecisionTaken) return;
    await dispatch(acceptAdminPrescription(prescriptionId));
  };

  const handleReject = async () => {
    if (!prescriptionId) return;
    if (isDecisionTaken) return;
    await dispatch(
      rejectAdminPrescription({
        prescriptionId,
        rejectionReason: "Please re-upload",
      })
    );
  };

  if (!prescriptionId) {
    return (
      <AdminLayout>
        <div className="patientSubmissionPage">
          <div className="patientSubmissionBackBar">
            <button
              type="button"
              className="patientSubmissionBackBtn"
              onClick={() => navigate("/prescriptions-reports")}
            >
              ← Clinical files
            </button>
          </div>
          <div className="patientSubmissionEmpty patientSubmissionEmpty--pad">
            No document is selected. Open a row from{" "}
            <button
              type="button"
              className="patientSubmissionInlineLink"
              onClick={() => navigate("/prescriptions-reports")}
            >
              Clinical files
            </button>{" "}
            to review uploads.
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="patientSubmissionPage">
        <div className="patientSubmissionBackBar">
          {returnTo === "clinical" ? (
            <button
              type="button"
              className="patientSubmissionBackBtn"
              onClick={goBackToClinical}
            >
              ← Back to Clinical files
            </button>
          ) : (
            <button
              type="button"
              className="patientSubmissionBackBtn patientSubmissionBackBtn--muted"
              onClick={handleClose}
            >
              ← Back
            </button>
          )}
        </div>

        {!detailLoading && (
          <div className="patientSubmissionHead">
            <div>
              <h1>Patient Medical Submissions</h1>
              <p>View uploaded prescriptions, reports, and diagnostic files.</p>
            </div>
          </div>
        )}

        {detailLoading && (
          <div
            className="patientSubmissionSkeleton"
            role="status"
            aria-busy="true"
            aria-live="polite"
          >
            <span className="patientSubmissionSkeletonSrOnly">Loading document details</span>
            <div className="patientSubmissionHead patientSubmissionHead--skeleton">
              <SkeletonText lines={2} lineHeight={14} gap={10} />
              <SkeletonBlock width={44} height={44} className="patientSubmissionSkeletonIcon" />
            </div>

            <div className="patientSubmissionUser patientSubmissionUser--skeleton">
              <SkeletonBlock
                width={72}
                height={72}
                className="patientSubmissionSkeletonAvatar"
              />
              <div className="patientSubmissionSkeletonUserText">
                <SkeletonBlock height={22} width="min(280px, 60%)" />
                <SkeletonBlock height={16} width="min(200px, 45%)" />
              </div>
            </div>

            <div className="patientSubmissionCard patientSubmissionCard--skeleton">
              <div className="patientSubmissionTabs patientSubmissionTabs--three patientSubmissionTabs--skeleton">
                <SkeletonBlock height={48} width="100%" />
                <SkeletonBlock height={48} width="100%" />
                <SkeletonBlock height={48} width="100%" />
              </div>

              <div className="patientSubmissionBody patientSubmissionBody--skeleton">
                <div className="patientSubmissionPreview">
                  <SkeletonBlock
                    height={240}
                    width="100%"
                    className="patientSubmissionSkeletonPreview"
                  />
                  <SkeletonBlock height={14} width={140} />
                </div>

                <div className="patientSubmissionDocs patientSubmissionDocs--skeleton">
                  <div className="patientSubmissionDocsTitle">
                    <SkeletonBlock width={24} height={24} />
                    <SkeletonBlock width={120} height={18} />
                  </div>
                  {[0, 1, 2].map((i) => (
                    <div className="patientSubmissionDocItem patientSubmissionDocItem--skeleton" key={i}>
                      <div className="patientSubmissionDocLeft">
                        <SkeletonBlock width={40} height={48} />
                        <div className="patientSubmissionSkeletonDocLines">
                          <SkeletonBlock height={16} width="85%" />
                          <SkeletonBlock height={13} width="40%" />
                          <SkeletonBlock height={12} width="55%" />
                        </div>
                      </div>
                      <SkeletonBlock width={72} height={36} className="patientSubmissionSkeletonViewBtn" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {!detailLoading && error && <div className="patientSubmissionEmpty">{error}</div>}
        {!detailLoading && successMessage && (
          <div className="patientSubmissionEmpty">{successMessage}</div>
        )}

        {!detailLoading && !error && (
          <>
            <div className="patientSubmissionTopStats">
              <div className="patientSubmissionStatCard">
                <span className="patientSubmissionStatLabel">Current tab</span>
                <strong>{currentTabLabel}</strong>
              </div>
              <div className="patientSubmissionStatCard">
                <span className="patientSubmissionStatLabel">Files in tab</span>
                <strong>{currentTabCount}</strong>
              </div>
              <div className="patientSubmissionStatCard">
                <span className="patientSubmissionStatLabel">Review state</span>
                <strong>{isAccepted ? "Accepted" : isRejected ? "Rejected" : "Pending review"}</strong>
              </div>
            </div>

            <div className="patientSubmissionCard">
              <div className="patientSubmissionUser">
                <AvatarImage
                  src={patient.avatar || fallbackPatient.avatar}
                  alt={patient.patientName}
                  name={patient.patientName}
                  className="patientSubmissionAvatar"
                />

                <div className="patientSubmissionUserInfo">
                  <h3>{patient.patientName}</h3>
                  {patient.location ? <p>{patient.location}</p> : null}
                  {requestCategorySubcategory ? (
                    <div className="patientSubmissionCategoryPill" title={requestCategorySubcategory}>
                      {requestCategorySubcategory}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="patientSubmissionTabs patientSubmissionTabs--three">
                <button
                  type="button"
                  className={`patientSubmissionTab ${
                    activeTab === "prescription" ? "active" : ""
                  }`}
                  onClick={() => setTab("prescription")}
                >
                  Prescription
                </button>

                <button
                  type="button"
                  className={`patientSubmissionTab ${
                    activeTab === "report" ? "active" : ""
                  }`}
                  onClick={() => setTab("report")}
                >
                  Medical Report
                </button>

                <button
                  type="button"
                  className={`patientSubmissionTab ${
                    activeTab === "insurance" ? "active" : ""
                  }`}
                  onClick={() => setTab("insurance")}
                >
                  Insurance
                </button>
              </div>

              <div className="patientSubmissionBody">
                <div className="patientSubmissionPreview">
                  {activePreviewDoc ? (
                    <>
                      <div className="patientSubmissionPreviewImageWrap">
                        {activePreviewSrc ? (
                          <img
                            src={activePreviewSrc}
                            alt={activePreviewDoc.fileName || "preview"}
                            className="patientSubmissionPreviewImage"
                          />
                        ) : (
                          <div className="patientSubmissionPreviewPlaceholder">
                            No image preview — use View to open the file.
                          </div>
                        )}
                      </div>
                      <div className="patientSubmissionPreviewMeta">
                        <p>{activePreviewDoc.uploadedAt || "Uploaded recently"}</p>
                        <span
                          className={`patientSubmissionStatusTag patientSubmissionStatusTag--${
                            activeStatus || "pending"
                          }`}
                        >
                          {isAccepted ? "Accepted" : isRejected ? "Rejected" : "Pending review"}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="patientSubmissionEmpty patientSubmissionEmpty--card">
                      <strong>No {currentTabNoun} uploaded for this care request.</strong>
                      <span>
                        Once the patient uploads a {currentTabNoun}, it will appear here for preview and review.
                      </span>
                    </div>
                  )}
                </div>

                <div className="patientSubmissionDocs">
                  <div className="patientSubmissionDocsTitle">
                    <span>Documents ({filteredDocuments.length})</span>
                  </div>

                  {filteredDocuments.map((doc) => (
                    <div
                      className={`patientSubmissionDocItem ${
                        String(activePreviewDoc?.id) === String(doc.id) ? "patientSubmissionDocItem--active" : ""
                      }`}
                      key={doc.id}
                      onClick={() => setActiveDocId(String(doc.id))}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setActiveDocId(String(doc.id));
                        }
                      }}
                    >
                      <div className="patientSubmissionDocLeft">
                        <div className="patientSubmissionDocPdf">
                          {String(doc.fileName || "").split(".").pop()?.slice(0, 4)?.toUpperCase() || "FILE"}
                        </div>

                        <div className="patientSubmissionDocInfo">
                          <h4>{doc.fileName}</h4>
                          {doc.fileSize ? <span>{doc.fileSize}</span> : null}
                          <p>{doc.uploadedAt}</p>
                        </div>
                      </div>

                      <button type="button" className="patientSubmissionViewBtn" onClick={() => handleView(doc)}>
                        View
                      </button>
                    </div>
                  ))}

                  {!filteredDocuments.length && (
                    <div className="patientSubmissionEmpty patientSubmissionEmpty--card">
                      <strong>No {currentTabNoun} uploaded yet.</strong>
                      <span>
                        There are no files in this section right now. Ask the patient to upload {currentTabNoun}
                        from the care request flow.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="patientSubmissionCloseWrap">
              {isDecisionTaken ? (
                <button
                  type="button"
                  className={`patientSubmissionCloseBtn ${isAccepted ? "patientSubmissionCloseBtn--accepted" : "patientSubmissionCloseBtn--secondary"}`}
                  disabled
                >
                  {isAccepted ? "Already accepted" : "Already rejected"}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="patientSubmissionCloseBtn"
                    onClick={handleAccept}
                    disabled={actionLoading || !filteredDocuments.length}
                  >
                    {actionLoading ? "Accepting..." : "Accept selected"}
                  </button>

                  <button
                    type="button"
                    className="patientSubmissionCloseBtn patientSubmissionCloseBtn--secondary"
                    onClick={handleReject}
                    disabled={actionLoading || !filteredDocuments.length}
                  >
                    {actionLoading ? "Rejecting..." : "Reject selected"}
                  </button>
                </>
              )}

              <button
                type="button"
                className="patientSubmissionCloseBtn patientSubmissionCloseBtn--ghost"
                onClick={handleClose}
              >
                {returnTo === "clinical" ? "Back to list" : "Close"}
              </button>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
